import { type MaybeRefOrGetter, onUnmounted, toValue, watchEffect } from 'vue';
import { useMounted } from '@lib/use-mounted';

export function useDocumentEventListener<K extends keyof DocumentEventMap>(
  eventName: K,
  handler: (event: DocumentEventMap[K]) => void,
  enabled?: MaybeRefOrGetter<boolean>
): void;
export function useDocumentEventListener(
  eventName: string,
  handler: (event: Event) => void,
  enabled?: MaybeRefOrGetter<boolean>
): void;
export function useDocumentEventListener<K extends keyof DocumentEventMap>(
  eventName: K,
  handler: (event: DocumentEventMap[K]) => void,
  enabled: MaybeRefOrGetter<boolean> = true
) {
  if (typeof document === 'undefined') return;

  const hasMounted = useMounted();

  watchEffect(() => toValue(enabled) && hasMounted.value && document.addEventListener(eventName, handler));
  watchEffect(() => !toValue(enabled) && document.removeEventListener(eventName, handler));
  onUnmounted(() => document.removeEventListener(eventName, handler));
}

export function useWindowEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  enabled?: MaybeRefOrGetter<boolean>
): void;
export function useWindowEventListener(
  eventName: string,
  handler: (event: Event) => void,
  enabled?: MaybeRefOrGetter<boolean>
): void;
export function useWindowEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  enabled: MaybeRefOrGetter<boolean> = true
) {
  if (typeof window === 'undefined') return;

  const hasMounted = useMounted();

  watchEffect(() => toValue(enabled) && hasMounted.value && window.addEventListener(eventName, handler));
  watchEffect(() => !toValue(enabled) && window.removeEventListener(eventName, handler));
  onUnmounted(() => window.removeEventListener(eventName, handler));
}

export function insideVueDevtools(element: Node): boolean {
  // Devtools only available in dev. Don't do check otherwise.
  if (!import.meta.env.DEV) return false;

  const devTools = document.querySelector('#__vue-devtools-container__');
  if (!devTools || !(element instanceof Node)) return false;
  if (!(element instanceof Node)) return false;

  return devTools === element || devTools.contains(element);
}
